/**
 * ConfirmModal — generic confirm dialog.
 *
 * Two flavours via `requireTypedConfirm`:
 *   - undefined → plain Cancel/Confirm
 *   - set string → user must type it into the field before Confirm enables
 *     (used for destructive actions like Delete account where the user types `DELETE`).
 *
 * On open the focus goes to Cancel (the safe choice; the typed field when there is one), and Escape
 * cancels — except while `busy`.
 */

import { useEffect, useRef, useState } from 'react';
import styles from './ConfirmModal.module.css';

export interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  /** When set, user must type this exact string before Confirm enables. */
  requireTypedConfirm?: string;
  /** A request is running: Confirm and Cancel are disabled. */
  busy?: boolean;
  /** Shown as an alert under the body (e.g. the request failed). */
  error?: string;
  'data-testid'?: string;
}

export function ConfirmModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  cancelLabel = 'Cancel',
  variant = 'default',
  requireTypedConfirm,
  busy = false,
  error,
  'data-testid': testId,
}: ConfirmModalProps): JSX.Element {
  const [typed, setTyped] = useState('');
  const confirmEnabled = !busy && (!requireTypedConfirm || typed === requireTypedConfirm);
  const confirmClass = `${styles.confirmBtn} ${variant === 'danger' ? styles.danger : ''}`.trim();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const escapeRef = useRef({ busy, onCancel });
  escapeRef.current = { busy, onCancel };

  useEffect(() => {
    if (!requireTypedConfirm) cancelRef.current?.focus();
    // Only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || escapeRef.current.busy) return;
      e.preventDefault();
      escapeRef.current.onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="confirm-modal-title" data-testid={testId}>
      <div className={styles.sheet}>
        <h2 id="confirm-modal-title" className={styles.title}>
          {title}
        </h2>
        <p className={styles.body}>{body}</p>
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}

        {requireTypedConfirm && (
          <input
            type="text"
            className={styles.typedInput}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={`Type ${requireTypedConfirm}`}
            aria-label={`Type ${requireTypedConfirm} to confirm`}
            data-testid="confirm-modal-typed-input"
            autoFocus
          />
        )}

        <div className={styles.actions}>
          <button
            ref={cancelRef}
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-modal-cancel"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            disabled={!confirmEnabled}
            data-testid="confirm-modal-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
